<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme, WALLPAPER_OPTIONS } from 'dashboard/helper/themeHelper';
import { emitter } from 'shared/helpers/mitt';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  DASHBOARD_FONT_OPTIONS,
  initializeDashboardFont,
  normalizeDashboardFont,
} from 'dashboard/helper/fontHelper';

defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);
const { uiSettings, updateUISettings } = useUISettings();

const copy = {
  eyebrow: 'Tema',
  title: 'Aparência',
  primaryColorTitle: 'Cor de destaque',
  primaryColorDescription: 'Define a cor usada no card ativo, botões e links.',
  colorModeTitle: 'Modo',
  fontTitle: 'Fonte do sistema',
  fontDescription: 'Escolha a tipografia usada em todo o Dashboard.',
  fontSample: 'Aa',
  darkSchemeTitle: 'Tom escuro',
  darkSchemeDescription:
    'Aparência do fundo quando o modo escuro estiver ativo.',
  previewTitle: 'Prévia da aparência',
  previewDescription: 'Veja como modo, destaque e tom escuro funcionam juntos.',
  previewBrand: 'Talkvia',
  previewConversations: 'Conversas',
  previewContacts: 'Contatos',
  previewReports: 'Relatórios',
  previewConversationTitle: 'Atendimento em andamento',
  previewConversationSubtitle: 'Visualização instantânea do tema',
  previewResolve: 'Resolver',
  previewIncomingMessage: 'Olá! Como podemos ajudar?',
  previewOutgoingMessage: 'Preciso de informações.',
  wallpaperTitle: 'Papel de parede',
  wallpaperDescription: 'Cor de fundo da área de conversa.',
  notificationsTitle: 'Notificações',
  notificationsDescription: 'Como e onde os avisos aparecem na tela.',
  notificationPositionTitle: 'Posição na tela',
  notificationPositionDescription:
    'Escolha onde os avisos devem aparecer sem atrapalhar seu atendimento.',
  previewNotification: 'Testar notificação',
  previewMessage: 'Prévia de notificação: este é o modelo selecionado.',
  navigationLabel: 'Seções de aparência',
  close: 'Fechar',
};

const primaryColors = [
  {
    key: 'iris',
    label: 'Iris',
    bg: '#5b5bd6',
    accent: '#5b5bd6',
    light: 'rgba(91,91,214,0.12)',
  },
  {
    key: 'blue',
    label: 'Azul',
    bg: '#2781f6',
    accent: '#2781f6',
    light: 'rgba(39,129,246,0.12)',
  },
  {
    key: 'green',
    label: 'Verde',
    bg: '#12a594',
    accent: '#12a594',
    light: 'rgba(18,165,148,0.12)',
  },
  {
    key: 'amber',
    label: 'Âmbar',
    bg: '#ffa31a',
    accent: '#ffa31a',
    light: 'rgba(255,163,26,0.12)',
  },
  {
    key: 'violet',
    label: 'Violeta',
    bg: '#6e56cf',
    accent: '#6e56cf',
    light: 'rgba(110,86,207,0.12)',
  },
  {
    key: 'ruby',
    label: 'Rosa',
    bg: '#ff265b',
    accent: '#ff265b',
    light: 'rgba(255,38,91,0.12)',
  },
];

const darkSchemes = [
  {
    key: 'default',
    label: 'Padrão',
    bg: '#1c1c1f',
    sidebar: '#141417',
    accent: '#28282d',
  },
  {
    key: 'mint',
    label: 'Mint',
    bg: '#0e1d21',
    sidebar: '#0d1a1d',
    accent: '#1a353c',
  },
  {
    key: 'navy',
    label: 'Navy',
    bg: '#1f2d46',
    sidebar: '#172236',
    accent: '#273958',
  },
  {
    key: 'mirage',
    label: 'Mirage',
    bg: '#121422',
    sidebar: '#0c1422',
    accent: '#192d48',
  },
  {
    key: 'cinder',
    label: 'Cinder',
    bg: '#1c1c20',
    sidebar: '#141417',
    accent: '#2b2b30',
  },
  {
    key: 'black',
    label: 'Black',
    bg: '#0d0d0f',
    sidebar: '#080809',
    accent: '#1a1a1e',
  },
];

const wallpaperOptions = [
  { key: 'none', label: 'Padrão', bg: null, pattern: null },
  {
    key: 'whatsapp',
    label: 'Verde WhatsApp',
    bg: WALLPAPER_OPTIONS.whatsapp.bg,
    pattern: null,
  },
  {
    key: 'beige',
    label: 'Areia',
    bg: WALLPAPER_OPTIONS.beige.bg,
    pattern: null,
  },
  { key: 'sky', label: 'Azul', bg: WALLPAPER_OPTIONS.sky.bg, pattern: null },
  {
    key: 'lavender',
    label: 'Lavanda',
    bg: WALLPAPER_OPTIONS.lavender.bg,
    pattern: null,
  },
  { key: 'rose', label: 'Rosa', bg: WALLPAPER_OPTIONS.rose.bg, pattern: null },
  {
    key: 'dark_pattern',
    label: 'WA Escuro',
    bg: WALLPAPER_OPTIONS.dark_pattern.bg,
    pattern: WALLPAPER_OPTIONS.dark_pattern.pattern,
  },
  {
    key: 'classic',
    label: 'WA Claro',
    bg: WALLPAPER_OPTIONS.classic.bg,
    pattern: WALLPAPER_OPTIONS.classic.pattern,
  },
];

const notificationStyles = [
  {
    key: 'stacked',
    label: 'Empilhadas',
    description: 'Compactas, uma sobre a outra.',
    icon: 'i-lucide-layers-3',
  },
  {
    key: 'expanded',
    label: 'Expandidas',
    description: 'Separadas e mais fáceis de ler.',
    icon: 'i-lucide-list',
  },
];

const notificationPositions = [
  {
    key: 'top-left',
    label: 'Superior esquerdo',
    icon: 'i-lucide-arrow-up-left',
  },
  { key: 'top-center', label: 'Superior centro', icon: 'i-lucide-arrow-up' },
  {
    key: 'top-right',
    label: 'Superior direito',
    icon: 'i-lucide-arrow-up-right',
  },
  { key: 'middle-left', label: 'Centro esquerdo', icon: 'i-lucide-arrow-left' },
  { key: 'center', label: 'Centro', icon: 'i-lucide-scan' },
  {
    key: 'middle-right',
    label: 'Centro direito',
    icon: 'i-lucide-arrow-right',
  },
  {
    key: 'bottom-left',
    label: 'Inferior esquerdo',
    icon: 'i-lucide-arrow-down-left',
  },
  {
    key: 'bottom-center',
    label: 'Inferior centro',
    icon: 'i-lucide-arrow-down',
  },
  {
    key: 'bottom-right',
    label: 'Inferior direito',
    icon: 'i-lucide-arrow-down-right',
  },
];

const selectedPrimaryColor = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.PRIMARY_COLOR) || 'blue'
);
const selectedColorScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);
const selectedDarkScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.DARK_COLOR_SCHEME) || 'default'
);
const selectedNotificationStyle = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_GROUP_STYLE) || 'expanded'
);
const selectedNotificationPosition = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_POSITION) || 'top-right'
);
const selectedWallpaper = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.CONVERSATION_WALLPAPER) || 'none'
);
const selectedDashboardFont = ref(
  normalizeDashboardFont(
    uiSettings.value?.dashboard_font ||
      LocalStorage.get(LOCAL_STORAGE_KEYS.DASHBOARD_FONT)
  )
);
const activePanel = ref('theme');

const appearancePanels = [
  { key: 'theme', label: 'Tema', icon: 'i-lucide-palette' },
  { key: 'conversation', label: 'Conversa', icon: 'i-lucide-message-square' },
  { key: 'notifications', label: 'Avisos', icon: 'i-lucide-bell-ring' },
];

const colorSchemeOptions = [
  { key: 'light', label: 'Claro', icon: 'i-lucide-sun' },
  { key: 'dark', label: 'Escuro', icon: 'i-lucide-moon' },
  { key: 'auto', label: 'Sistema', icon: 'i-lucide-monitor' },
];

const selectedPrimaryColorOption = computed(
  () =>
    primaryColors.find(color => color.key === selectedPrimaryColor.value) ||
    primaryColors[1]
);
const selectedDarkSchemeOption = computed(
  () =>
    darkSchemes.find(scheme => scheme.key === selectedDarkScheme.value) ||
    darkSchemes[0]
);
const previewUsesDarkMode = computed(() => {
  if (selectedColorScheme.value === 'dark') return true;
  if (selectedColorScheme.value === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
});
const previewPalette = computed(() => {
  const accent = selectedPrimaryColorOption.value.accent;
  if (previewUsesDarkMode.value) {
    return {
      background: selectedDarkSchemeOption.value.bg,
      sidebar: selectedDarkSchemeOption.value.sidebar,
      surface: selectedDarkSchemeOption.value.accent,
      text: '#f4f4f5',
      muted: '#a1a1aa',
      accent,
    };
  }

  return {
    background: '#f7f8fa',
    sidebar: '#ffffff',
    surface: '#ffffff',
    text: '#202124',
    muted: '#71717a',
    accent,
  };
});

let themeTransitionAnimation = null;
const startThemeTransition = () => {
  themeTransitionAnimation?.cancel();
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    typeof document.body.animate !== 'function'
  ) {
    return;
  }

  themeTransitionAnimation = document.body.animate(
    [
      { opacity: 0.94, filter: 'brightness(0.98)' },
      { opacity: 1, filter: 'brightness(1)' },
    ],
    { duration: 240, easing: 'ease-out' }
  );
};

const applyPreferences = () => {
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.PRIMARY_COLOR,
    selectedPrimaryColor.value
  );
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, selectedColorScheme.value);
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.DARK_COLOR_SCHEME,
    selectedDarkScheme.value
  );
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.NOTIFICATION_GROUP_STYLE,
    selectedNotificationStyle.value
  );
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.NOTIFICATION_POSITION,
    selectedNotificationPosition.value
  );
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.CONVERSATION_WALLPAPER,
    selectedWallpaper.value
  );
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};

watch(
  [
    selectedPrimaryColor,
    selectedColorScheme,
    selectedDarkScheme,
    selectedNotificationStyle,
    selectedNotificationPosition,
    selectedWallpaper,
  ],
  applyPreferences
);
watch(selectedColorScheme, startThemeTransition, { flush: 'post' });

onUnmounted(() => {
  themeTransitionAnimation?.cancel();
});

const close = () => emit('close');
const selectDashboardFont = font => {
  selectedDashboardFont.value = initializeDashboardFont(font);
  updateUISettings({ dashboard_font: selectedDashboardFont.value });
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};

const previewNotification = () => {
  const previewItems =
    selectedNotificationStyle.value === 'stacked'
      ? [
          { message: copy.previewMessage, variant: 'info' },
          { message: 'Novo atendimento recebido.', variant: 'success' },
          { message: 'Uma tarefa precisa da sua atenção.', variant: 'warning' },
        ]
      : [{ message: copy.previewMessage, variant: 'info' }];

  previewItems.forEach((item, index) => {
    window.setTimeout(() => {
      emitter.emit('newToastMessage', {
        message: item.message,
        action: { variant: item.variant, duration: 7000 },
      });
    }, index * 90);
  });
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-md"
      @click.self="close"
    >
      <section
        class="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
      >
        <!-- Header -->
        <header class="flex items-center justify-between gap-4 px-6 py-5">
          <div class="flex items-center gap-3">
            <div
              class="grid size-9 place-content-center rounded-xl bg-n-brand/10"
            >
              <Icon icon="i-lucide-palette" class="size-5 text-n-brand" />
            </div>
            <div>
              <p
                class="text-[10px] font-semibold uppercase tracking-widest text-n-brand"
              >
                {{ copy.eyebrow }}
              </p>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ copy.title }}
              </h2>
            </div>
          </div>
          <button
            type="button"
            class="grid size-8 place-content-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            :aria-label="copy.close"
            :title="copy.close"
            @click.stop.prevent="close"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </header>

        <nav
          class="flex gap-1 border-y border-n-weak bg-n-alpha-1 px-6 py-2"
          :aria-label="copy.navigationLabel"
        >
          <button
            v-for="panel in appearancePanels"
            :key="panel.key"
            type="button"
            class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition"
            :class="
              activePanel === panel.key
                ? 'bg-n-solid-1 text-n-brand shadow-sm ring-1 ring-n-weak'
                : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            @click="activePanel = panel.key"
          >
            <Icon :icon="panel.icon" class="size-4" />
            {{ panel.label }}
          </button>
        </nav>

        <div class="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          <div class="flex flex-col gap-6">
            <!-- Primary color -->
            <section v-show="activePanel === 'theme'" class="order-3">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.primaryColorTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.primaryColorDescription }}
                </p>
              </div>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <button
                  v-for="color in primaryColors"
                  :key="color.key"
                  type="button"
                  class="group flex flex-col items-center gap-2 rounded-xl border p-3 transition-all"
                  :class="
                    selectedPrimaryColor === color.key
                      ? 'border-transparent ring-2'
                      : 'border-n-weak hover:border-n-slate-7'
                  "
                  :style="
                    selectedPrimaryColor === color.key
                      ? {
                          ringColor: color.accent,
                          borderColor: 'transparent',
                          boxShadow: `0 0 0 2px ${color.accent}`,
                        }
                      : {}
                  "
                  :title="color.label"
                  @click="selectedPrimaryColor = color.key"
                >
                  <!-- Mini card preview showing the accent bar -->
                  <div
                    class="w-full rounded-lg overflow-hidden border border-n-weak/60"
                    :style="{ backgroundColor: color.light }"
                  >
                    <div class="flex items-center gap-1.5 px-2 py-2">
                      <div
                        class="h-6 w-1 flex-shrink-0 rounded-full"
                        :style="{ backgroundColor: color.accent }"
                      />
                      <div class="flex flex-col gap-1 flex-1 min-w-0">
                        <div
                          class="h-1.5 rounded-full w-full opacity-60"
                          :style="{ backgroundColor: color.accent }"
                        />
                        <div
                          class="h-1 rounded-full w-3/4 opacity-30"
                          :style="{ backgroundColor: color.accent }"
                        />
                      </div>
                    </div>
                  </div>
                  <span class="text-[10px] font-medium text-n-slate-11">{{
                    color.label
                  }}</span>
                  <Icon
                    v-if="selectedPrimaryColor === color.key"
                    icon="i-lucide-check-circle-2"
                    class="size-3.5"
                    :style="{ color: color.accent }"
                  />
                  <div v-else class="size-3.5" />
                </button>
              </div>
            </section>

            <!-- Color mode -->
            <section v-show="activePanel === 'theme'" class="order-1">
              <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
                {{ copy.colorModeTitle }}
              </h3>
              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="option in colorSchemeOptions"
                  :key="option.key"
                  type="button"
                  class="flex flex-col items-center gap-2 rounded-xl border px-3 py-3 transition-all"
                  :class="
                    selectedColorScheme === option.key
                      ? 'border-n-brand bg-n-brand/8 text-n-brand'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-11 hover:border-n-slate-7 hover:text-n-slate-12'
                  "
                  @click="selectedColorScheme = option.key"
                >
                  <Icon :icon="option.icon" class="size-5" />
                  <span class="text-xs font-medium">{{ option.label }}</span>
                </button>
              </div>
            </section>

            <!-- Dashboard font -->
            <section v-show="activePanel === 'theme'" class="order-2">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.fontTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.fontDescription }}
                </p>
              </div>
              <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <button
                  v-for="font in DASHBOARD_FONT_OPTIONS"
                  :key="font.key"
                  type="button"
                  class="flex items-center gap-3 rounded-xl border bg-n-solid-2 px-4 py-3 text-left transition-all"
                  :class="
                    selectedDashboardFont === font.key
                      ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/15'
                      : 'border-n-weak hover:border-n-slate-7'
                  "
                  :aria-pressed="selectedDashboardFont === font.key"
                  @click="selectDashboardFont(font.key)"
                >
                  <span
                    class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-lg font-semibold text-n-slate-12"
                    :style="{ fontFamily: font.family }"
                  >
                    {{ copy.fontSample }}
                  </span>
                  <span class="min-w-0 flex-1">
                    <span
                      class="block text-sm font-semibold text-n-slate-12"
                      :style="{ fontFamily: font.family }"
                    >
                      {{ font.label }}
                    </span>
                    <span class="block text-xs text-n-slate-10">
                      {{ font.description }}
                    </span>
                  </span>
                  <Icon
                    v-if="selectedDashboardFont === font.key"
                    icon="i-lucide-circle-check"
                    class="size-4 shrink-0 text-n-brand"
                  />
                </button>
              </div>
            </section>

            <!-- Dark scheme -->
            <section v-show="activePanel === 'theme'" class="order-4">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.darkSchemeTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.darkSchemeDescription }}
                </p>
              </div>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <button
                  v-for="scheme in darkSchemes"
                  :key="scheme.key"
                  type="button"
                  class="flex flex-col items-center gap-2 rounded-xl border p-2.5 transition-all"
                  :class="
                    selectedDarkScheme === scheme.key
                      ? 'border-n-brand'
                      : 'border-n-weak hover:border-n-slate-7'
                  "
                  @click="selectedDarkScheme = scheme.key"
                >
                  <!-- Mini dark UI preview -->
                  <div
                    class="w-full overflow-hidden rounded-lg"
                    :style="{ backgroundColor: scheme.bg }"
                  >
                    <div class="flex gap-0.5 p-1">
                      <!-- sidebar sliver -->
                      <div
                        class="min-h-7 w-3 rounded-sm flex-shrink-0"
                        :style="{ backgroundColor: scheme.sidebar }"
                      />
                      <!-- content area -->
                      <div
                        class="flex-1 rounded-sm p-1 flex flex-col gap-1"
                        :style="{ backgroundColor: scheme.accent }"
                      >
                        <div class="h-1 rounded-full bg-white opacity-40" />
                        <div
                          class="h-1 rounded-full w-2/3 bg-white opacity-25"
                        />
                      </div>
                    </div>
                  </div>
                  <span class="text-[10px] font-medium text-n-slate-11">{{
                    scheme.label
                  }}</span>
                  <Icon
                    v-if="selectedDarkScheme === scheme.key"
                    icon="i-lucide-check-circle-2"
                    class="size-3.5 text-n-brand"
                  />
                  <div v-else class="size-3.5" />
                </button>
              </div>
            </section>

            <!-- Theme preview -->
            <section v-show="activePanel === 'theme'" class="order-5">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.previewTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.previewDescription }}
                </p>
              </div>
              <div
                class="overflow-hidden rounded-2xl border border-n-weak shadow-sm transition-colors duration-300"
                :style="{ backgroundColor: previewPalette.background }"
              >
                <div class="flex min-h-48">
                  <aside
                    class="w-32 shrink-0 border-r border-black/10 p-3 transition-colors duration-300"
                    :style="{ backgroundColor: previewPalette.sidebar }"
                  >
                    <div class="mb-5 flex items-center gap-2">
                      <span
                        class="grid size-7 place-content-center rounded-lg text-white"
                        :style="{ backgroundColor: previewPalette.accent }"
                      >
                        <Icon icon="i-lucide-message-square" class="size-4" />
                      </span>
                      <span
                        class="text-xs font-semibold"
                        :style="{ color: previewPalette.text }"
                      >
                        {{ copy.previewBrand }}
                      </span>
                    </div>
                    <div class="space-y-2">
                      <div
                        class="rounded-lg px-2 py-2 text-[10px] font-semibold text-white"
                        :style="{ backgroundColor: previewPalette.accent }"
                      >
                        {{ copy.previewConversations }}
                      </div>
                      <div
                        class="px-2 py-1 text-[10px]"
                        :style="{ color: previewPalette.muted }"
                      >
                        {{ copy.previewContacts }}
                      </div>
                      <div
                        class="px-2 py-1 text-[10px]"
                        :style="{ color: previewPalette.muted }"
                      >
                        {{ copy.previewReports }}
                      </div>
                    </div>
                  </aside>
                  <div class="min-w-0 flex-1 p-4">
                    <div class="mb-4 flex items-center justify-between">
                      <div>
                        <p
                          class="text-xs font-semibold"
                          :style="{ color: previewPalette.text }"
                        >
                          {{ copy.previewConversationTitle }}
                        </p>
                        <p
                          class="text-[10px]"
                          :style="{ color: previewPalette.muted }"
                        >
                          {{ copy.previewConversationSubtitle }}
                        </p>
                      </div>
                      <button
                        type="button"
                        class="rounded-lg px-3 py-1.5 text-[10px] font-semibold text-white"
                        :style="{ backgroundColor: previewPalette.accent }"
                      >
                        {{ copy.previewResolve }}
                      </button>
                    </div>
                    <div class="space-y-2">
                      <div
                        class="max-w-[75%] rounded-xl border border-black/10 px-3 py-2 text-[10px] shadow-sm transition-colors duration-300"
                        :style="{
                          backgroundColor: previewPalette.surface,
                          color: previewPalette.text,
                        }"
                      >
                        {{ copy.previewIncomingMessage }}
                      </div>
                      <div
                        class="ml-auto max-w-[70%] rounded-xl px-3 py-2 text-[10px] text-white"
                        :style="{ backgroundColor: previewPalette.accent }"
                      >
                        {{ copy.previewOutgoingMessage }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Wallpaper -->
            <section v-show="activePanel === 'conversation'">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.wallpaperTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.wallpaperDescription }}
                </p>
              </div>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <button
                  v-for="option in wallpaperOptions"
                  :key="option.key"
                  type="button"
                  class="flex flex-col items-center gap-2 rounded-xl border p-2.5 transition-all"
                  :class="
                    selectedWallpaper === option.key
                      ? 'border-n-brand'
                      : 'border-n-weak hover:border-n-slate-7'
                  "
                  @click="selectedWallpaper = option.key"
                >
                  <!-- Wallpaper preview tile -->
                  <div
                    class="h-10 w-full overflow-hidden rounded-lg border border-n-weak/60"
                    :style="
                      option.bg
                        ? {
                            backgroundColor: option.bg,
                            backgroundImage: option.pattern || 'none',
                            backgroundRepeat: 'repeat',
                            backgroundSize: 'auto',
                          }
                        : {}
                    "
                    :class="!option.bg ? 'bg-n-surface-2' : ''"
                  >
                    <div
                      v-if="!option.bg"
                      class="flex items-center justify-center h-full"
                    >
                      <Icon
                        icon="i-lucide-slash"
                        class="size-3 text-n-slate-8"
                      />
                    </div>
                    <div
                      v-else
                      class="h-full flex items-end px-1.5 pb-1.5 gap-1"
                    >
                      <div class="h-2.5 w-6 rounded-full bg-white/70" />
                      <div class="h-2.5 w-5 rounded-full bg-white/50 ml-auto" />
                    </div>
                  </div>
                  <span
                    class="text-[10px] font-medium text-n-slate-11 text-center leading-tight"
                  >
                    {{ option.label }}
                  </span>
                  <Icon
                    v-if="selectedWallpaper === option.key"
                    icon="i-lucide-check-circle-2"
                    class="size-3.5 text-n-brand"
                  />
                  <div v-else class="size-3.5" />
                </button>
              </div>
            </section>

            <!-- Notifications -->
            <section
              v-show="activePanel === 'notifications'"
              class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2"
            >
              <div
                class="flex items-start gap-3 border-b border-n-weak bg-n-alpha-1 px-4 py-3"
              >
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
                >
                  <Icon icon="i-lucide-bell-ring" class="size-4" />
                </span>
                <div class="min-w-0 flex-1">
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ copy.notificationsTitle }}
                  </h3>
                  <p class="mb-0 mt-0.5 text-xs leading-5 text-n-slate-10">
                    {{ copy.notificationsDescription }}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="faded"
                  color="slate"
                  icon="i-lucide-play"
                  :label="copy.previewNotification"
                  @click="previewNotification"
                />
              </div>
              <div class="grid gap-3 p-4 sm:grid-cols-2">
                <button
                  v-for="style in notificationStyles"
                  :key="style.key"
                  type="button"
                  class="group relative rounded-2xl border p-3 text-left transition-all"
                  :class="
                    selectedNotificationStyle === style.key
                      ? 'border-n-brand bg-n-brand/5 shadow-sm ring-2 ring-n-brand/15'
                      : 'border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-1'
                  "
                  :aria-pressed="selectedNotificationStyle === style.key"
                  @click="selectedNotificationStyle = style.key"
                >
                  <div
                    class="mb-3 h-24 overflow-hidden rounded-xl border border-n-weak bg-n-background p-3"
                  >
                    <div v-if="style.key === 'stacked'" class="relative h-full">
                      <div
                        class="absolute left-5 right-0 top-1 h-12 rounded-lg border border-n-weak bg-n-solid-1 opacity-45 shadow-sm"
                      />
                      <div
                        class="absolute left-2 right-3 top-3 h-12 rounded-lg border border-n-weak bg-n-solid-1 opacity-70 shadow-sm"
                      />
                      <div
                        class="absolute inset-x-0 bottom-0 flex h-12 items-center gap-2 rounded-lg border border-n-strong bg-n-solid-2 px-2.5 shadow-md"
                      >
                        <span
                          class="grid size-6 shrink-0 place-items-center rounded-md bg-n-blue-3 text-n-blue-10"
                        >
                          <span class="i-lucide-message-circle size-3.5" />
                        </span>
                        <span class="min-w-0 flex-1 space-y-1.5">
                          <span
                            class="block h-1.5 w-2/3 rounded-full bg-n-slate-8"
                          />
                          <span
                            class="block h-1.5 w-full rounded-full bg-n-slate-5"
                          />
                        </span>
                      </div>
                    </div>
                    <div v-else class="grid gap-2">
                      <div
                        v-for="tone in ['blue', 'amber']"
                        :key="tone"
                        class="flex h-8 items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-2 shadow-sm"
                      >
                        <span
                          class="size-2 shrink-0 rounded-full"
                          :class="
                            tone === 'blue' ? 'bg-n-blue-9' : 'bg-n-amber-9'
                          "
                        />
                        <span class="min-w-0 flex-1 space-y-1">
                          <span
                            class="block h-1.5 w-1/2 rounded-full bg-n-slate-8"
                          />
                          <span
                            class="block h-1.5 w-4/5 rounded-full bg-n-slate-5"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-start gap-2.5 px-1 pb-1">
                    <span
                      class="grid size-8 shrink-0 place-items-center rounded-lg"
                      :class="
                        selectedNotificationStyle === style.key
                          ? 'bg-n-brand text-white'
                          : 'bg-n-alpha-2 text-n-slate-10 group-hover:text-n-slate-12'
                      "
                    >
                      <Icon :icon="style.icon" class="size-4" />
                    </span>
                    <div class="min-w-0 flex-1">
                      <div class="text-sm font-semibold text-n-slate-12">
                        {{ style.label }}
                      </div>
                      <div class="mt-0.5 text-xs leading-4 text-n-slate-10">
                        {{ style.description }}
                      </div>
                    </div>
                    <Icon
                      :icon="
                        selectedNotificationStyle === style.key
                          ? 'i-lucide-circle-check'
                          : 'i-lucide-circle'
                      "
                      class="mt-1 size-4 shrink-0"
                      :class="
                        selectedNotificationStyle === style.key
                          ? 'text-n-brand'
                          : 'text-n-slate-8'
                      "
                    />
                  </div>
                </button>
              </div>
              <div class="border-t border-n-weak p-4">
                <div class="mb-3">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ copy.notificationPositionTitle }}
                  </h4>
                  <p class="mb-0 mt-0.5 text-xs leading-5 text-n-slate-10">
                    {{ copy.notificationPositionDescription }}
                  </p>
                </div>
                <div
                  class="grid grid-cols-3 gap-2 rounded-xl border border-n-weak bg-n-background p-2"
                >
                  <button
                    v-for="position in notificationPositions"
                    :key="position.key"
                    type="button"
                    class="group flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-center transition"
                    :class="
                      selectedNotificationPosition === position.key
                        ? 'border-n-brand bg-n-brand/10 text-n-brand shadow-sm'
                        : 'border-transparent bg-n-solid-1 text-n-slate-10 hover:border-n-strong hover:text-n-slate-12'
                    "
                    :aria-label="position.label"
                    :aria-pressed="
                      selectedNotificationPosition === position.key
                    "
                    @click="selectedNotificationPosition = position.key"
                  >
                    <Icon :icon="position.icon" class="size-4" />
                    <span class="text-[10px] font-medium leading-3">
                      {{ position.label }}
                    </span>
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>

        <footer class="flex justify-end border-t border-n-weak px-6 py-4">
          <Button
            type="button"
            variant="faded"
            color="slate"
            :label="copy.close"
            @click="close"
          />
        </footer>
      </section>
    </div>
  </Teleport>
</template>
